// Iter206 — Ritaglio prospettico + filtro B/N per lo Scanner (mobile).
// Usato dopo ogni foto scattata dal flow Scanner in FormFillerDialog.
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { loadJscanify, loadOpenCV } from "@/lib/scannerLoader";

// Applica un filtro "documento" (grayscale + soglia adattiva) a un canvas.
function applyDocumentFilter(canvas) {
  const ctx = canvas.getContext("2d");
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  // Grayscale + curva contrasto morbida (soglia locale approssimata)
  for (let i = 0; i < d.length; i += 4) {
    const g = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    // Curva contrast + boost bianchi
    const v = g < 128 ? Math.max(0, g * 0.85) : Math.min(255, 40 + g * 1.15);
    d[i] = d[i + 1] = d[i + 2] = v;
  }
  ctx.putImageData(img, 0, 0);
}

export default function ScanCropStage({ open, imageDataUrl, onCancel, onConfirm }) {
  const workRef = useRef(null); // hidden work canvas
  const previewRef = useRef(null); // canvas visibile (immagine originale + handles)
  const [corners, setCorners] = useState(null); // {tl,tr,br,bl}
  const [scale, setScale] = useState(1);
  const [natSize, setNatSize] = useState({ w: 0, h: 0 });
  const [processing, setProcessing] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [mode, setMode] = useState("document"); // "document" | "photo"

  // Boot: carica opencv+jscanify, disegna immagine + rileva bordi
  useEffect(() => {
    if (!open || !imageDataUrl) return;
    let cancelled = false;
    setProcessing(true);
    (async () => {
      try {
        await loadJscanify();
        const img = await new Promise((res, rej) => {
          const im = new Image();
          im.onload = () => res(im);
          im.onerror = rej;
          im.src = imageDataUrl;
        });
        if (cancelled) return;
        // Preview scaled to max width 640 for touch
        const canvas = previewRef.current;
        if (!canvas) return;
        const maxW = Math.min(640, window.innerWidth - 40);
        const s = Math.min(1, maxW / img.width);
        canvas.width = Math.round(img.width * s);
        canvas.height = Math.round(img.height * s);
        setScale(s);
        setNatSize({ w: img.width, h: img.height });
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // Detect corners via jscanify (usa una copia natural-size in hidden canvas)
        const work = workRef.current;
        work.width = img.width;
        work.height = img.height;
        work.getContext("2d").drawImage(img, 0, 0);
        try {
          const scanner = new window.jscanify();
          const box = scanner.getCornerPoints(work); // {topLeftCorner: {x,y}, ...}
          if (box) {
            setCorners({
              tl: { x: box.topLeftCorner.x * s, y: box.topLeftCorner.y * s },
              tr: { x: box.topRightCorner.x * s, y: box.topRightCorner.y * s },
              br: { x: box.bottomRightCorner.x * s, y: box.bottomRightCorner.y * s },
              bl: { x: box.bottomLeftCorner.x * s, y: box.bottomLeftCorner.y * s },
            });
          } else {
            // Fallback: 4 angoli con margine 5%
            const m = 0.05;
            setCorners({
              tl: { x: canvas.width * m, y: canvas.height * m },
              tr: { x: canvas.width * (1 - m), y: canvas.height * m },
              br: { x: canvas.width * (1 - m), y: canvas.height * (1 - m) },
              bl: { x: canvas.width * m, y: canvas.height * (1 - m) },
            });
          }
        } catch {
          const m = 0.05;
          setCorners({
            tl: { x: canvas.width * m, y: canvas.height * m },
            tr: { x: canvas.width * (1 - m), y: canvas.height * m },
            br: { x: canvas.width * (1 - m), y: canvas.height * (1 - m) },
            bl: { x: canvas.width * m, y: canvas.height * (1 - m) },
          });
        }
      } catch (e) {
        // opencv/jscanify load failed → fallback pass-through
        onConfirm(imageDataUrl);
      } finally {
        if (!cancelled) setProcessing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, imageDataUrl]);

  // Overlay draw: linee tra i 4 handle + pallini
  useEffect(() => {
    if (!corners || !previewRef.current) return;
    const c = previewRef.current;
    const ctx = c.getContext("2d");
    // Redraw immagine base
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, c.width, c.height);
      ctx.strokeStyle = "#7c3aed";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(corners.tl.x, corners.tl.y);
      ctx.lineTo(corners.tr.x, corners.tr.y);
      ctx.lineTo(corners.br.x, corners.br.y);
      ctx.lineTo(corners.bl.x, corners.bl.y);
      ctx.closePath();
      ctx.stroke();
    };
    img.src = imageDataUrl;
  }, [corners, imageDataUrl]);

  const clampToCanvas = (x, y) => {
    const c = previewRef.current;
    return { x: Math.max(0, Math.min(c.width, x)), y: Math.max(0, Math.min(c.height, y)) };
  };

  const onPointerDown = (which, e) => { e.preventDefault(); setDragging(which); };
  const onPointerMove = (e) => {
    if (!dragging) return;
    const c = previewRef.current;
    const rect = c.getBoundingClientRect();
    const evt = e.touches ? e.touches[0] : e;
    const pos = clampToCanvas(evt.clientX - rect.left, evt.clientY - rect.top);
    setCorners((prev) => ({ ...prev, [dragging]: pos }));
  };
  const onPointerUp = () => setDragging(null);

  const confirm = async () => {
    if (!corners) return;
    try {
      await loadOpenCV();
      const cv = window.cv;
      // Corners in coord natural
      const inv = 1 / scale;
      const src = cv.matFromArray(4, 1, cv.CV_32FC2, [
        corners.tl.x * inv, corners.tl.y * inv,
        corners.tr.x * inv, corners.tr.y * inv,
        corners.br.x * inv, corners.br.y * inv,
        corners.bl.x * inv, corners.bl.y * inv,
      ]);
      const wA4 = Math.max(natSize.w, natSize.h) * 0.71; // A4 ratio
      const hA4 = wA4 * 1.4142;
      const outW = Math.round(wA4);
      const outH = Math.round(hA4);
      const dst = cv.matFromArray(4, 1, cv.CV_32FC2, [
        0, 0,
        outW, 0,
        outW, outH,
        0, outH,
      ]);
      const M = cv.getPerspectiveTransform(src, dst);
      // Legge immagine natural-size dal work canvas
      const work = workRef.current;
      const srcMat = cv.imread(work);
      const outMat = new cv.Mat();
      cv.warpPerspective(srcMat, outMat, M, new cv.Size(outW, outH));
      // Disegna su canvas out
      const outCanvas = document.createElement("canvas");
      outCanvas.width = outW;
      outCanvas.height = outH;
      cv.imshow(outCanvas, outMat);
      srcMat.delete(); outMat.delete(); M.delete(); src.delete(); dst.delete();
      // Filtro
      if (mode === "document") applyDocumentFilter(outCanvas);
      const dataUrl = outCanvas.toDataURL("image/jpeg", 0.9);
      onConfirm(dataUrl);
    } catch (e) {
      // Fallback: passa la foto originale
      onConfirm(imageDataUrl);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="scan-crop-modal">
        <DialogHeader>
          <DialogTitle>Ritaglia il foglio</DialogTitle>
          <DialogDescription>
            {processing ? "Rilevamento bordi in corso…" : "Trascina i punti agli angoli del foglio se necessario."}
          </DialogDescription>
        </DialogHeader>
        <div
          className="relative select-none"
          onMouseMove={onPointerMove}
          onMouseUp={onPointerUp}
          onMouseLeave={onPointerUp}
          onTouchMove={onPointerMove}
          onTouchEnd={onPointerUp}
        >
          <canvas ref={previewRef} className="w-full rounded border" />
          <canvas ref={workRef} className="hidden" />
          {corners && !processing && ["tl", "tr", "br", "bl"].map((k) => {
            const p = corners[k];
            return (
              <div
                key={k}
                onMouseDown={(e) => onPointerDown(k, e)}
                onTouchStart={(e) => onPointerDown(k, e)}
                data-testid={`scan-crop-handle-${k}`}
                style={{
                  position: "absolute",
                  left: p.x - 12,
                  top: p.y - 12,
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: "#7c3aed",
                  border: "2px solid #fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
                  cursor: "grab",
                  touchAction: "none",
                }}
              />
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-2 mt-3" data-testid="scan-crop-mode">
          <button
            type="button"
            onClick={() => setMode("document")}
            className={`h-8 px-3 rounded text-xs font-semibold border ${mode === "document" ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-700 border-slate-300"}`}
            data-testid="scan-crop-mode-document"
          >
            📄 Documento
          </button>
          <button
            type="button"
            onClick={() => setMode("photo")}
            className={`h-8 px-3 rounded text-xs font-semibold border ${mode === "photo" ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-700 border-slate-300"}`}
            data-testid="scan-crop-mode-photo"
          >
            🖼️ Foto
          </button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} data-testid="scan-crop-cancel">Annulla</Button>
          <Button
            onClick={confirm}
            disabled={processing || !corners}
            className="ht-gradient text-white"
            data-testid="scan-crop-confirm"
          >
            Conferma ritaglio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
